import { ColorsType } from '_/interfaces/ColorsType';
import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, useWindowDimensions } from 'react-native';

const createCornerAnimation = (animatedValue) => {
  return Animated.loop(
    Animated.sequence([
      Animated.timing(animatedValue, {
        toValue: 1,
        duration: 750,
        useNativeDriver: true,
      }),
      Animated.timing(animatedValue, {
        toValue: 0,
        duration: 750,
        useNativeDriver: true,
      }),
    ]),
    {
      iterations: -1,
    }
  );
};

const AnimatedCorner = ({ animatedValue, style, color }) => {
  const animatedStyle = {
    transform: [
      {
        translateX: animatedValue.interpolate({
          inputRange: [0, 1],
          outputRange: style.left !== undefined ? [0, -10] : [0, 10],
        }),
      },
      {
        translateY: animatedValue.interpolate({
          inputRange: [0, 1],
          outputRange: style.top !== undefined ? [0, -10] : [0, 10],
        }),
      },
    ],
  };

  return <Animated.View style={[styles.corner, style, animatedStyle, { borderColor: color }]} />;
};
const FaceScanner = ({
  isFaceDetected,
  colors,
}: {
  isFaceDetected: boolean;
  colors: ColorsType;
}) => {
  const { width, height } = useWindowDimensions();
  const isPortrait = height >= width;

  const frameSize = isPortrait ? width * 0.6 : height * 0.4;

  const translate = frameSize / 2;
  const animatedValues = useRef({
    topLeft: new Animated.Value(0),
    topRight: new Animated.Value(0),
    bottomLeft: new Animated.Value(0),
    bottomRight: new Animated.Value(0),
  }).current;

  useEffect(() => {
    const topLeftAnim = createCornerAnimation(animatedValues.topLeft);
    const topRightAnim = createCornerAnimation(animatedValues.topRight);
    const bottomLeftAnim = createCornerAnimation(animatedValues.bottomLeft);
    const bottomRightAnim = createCornerAnimation(animatedValues.bottomRight);

    topLeftAnim.start();
    topRightAnim.start();
    bottomLeftAnim.start();
    bottomRightAnim.start();

    return () => {
      topLeftAnim.stop();
      topRightAnim.stop();
      bottomLeftAnim.stop();
      bottomRightAnim.stop();
    };
  }, []);

  return (
    <View
      style={[
        styles.frame,
        {
          width: frameSize,
          height: frameSize,
          transform: [{ translateX: -translate }, { translateY: -translate }],
        },
      ]}
    >
      <AnimatedCorner
        animatedValue={animatedValues.topLeft}
        style={styles.topLeft}
        color={isFaceDetected ? colors.success : colors.primary}
      />
      <AnimatedCorner
        animatedValue={animatedValues.topRight}
        style={styles.topRight}
        color={isFaceDetected ? colors.success : colors.primary}
      />
      <AnimatedCorner
        animatedValue={animatedValues.bottomLeft}
        style={styles.bottomLeft}
        color={isFaceDetected ? colors.success : colors.primary}
      />
      <AnimatedCorner
        animatedValue={animatedValues.bottomRight}
        style={styles.bottomRight}
        color={isFaceDetected ? colors.success : colors.primary}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  frame: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    pointerEvents: 'none',
    zIndex: 20,
  },
  corner: {
    position: 'absolute',
    width: 40,
    height: 40,
    borderWidth: 4,
    opacity: 0.8,
  },
  topLeft: {
    top: 0,
    left: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  topRight: {
    top: 0,
    right: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
  },
  bottomLeft: {
    bottom: 0,
    left: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
  },
  bottomRight: {
    bottom: 0,
    right: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
});

export default FaceScanner;
